import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Download, FileText, Trash2, Calendar, Send, Eye, Flame, CheckCircle2, Copy, GitBranch } from "lucide-react";
import { toast } from "sonner";
import PropostaNativaForm from "./PropostaNativaForm";
import { NovaVersaoDialog } from "./NovaVersaoDialog";

const fmtBRL = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const STATUS_COLOR = {
  rascunho: "bg-slate-100 text-slate-700",
  gerada: "bg-blue-100 text-blue-700",
  enviada: "bg-amber-100 text-amber-700",
  assinada: "bg-emerald-100 text-emerald-700",
  aprovada: "bg-emerald-100 text-emerald-700",
  recusada: "bg-red-100 text-red-700",
  expirada: "bg-gray-100 text-gray-500",
};

const TIPO_LABEL = { locacao: "Locação", venda: "Venda", servico: "Serviço", outros: "Outros" };

const fmtData = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR") + " " + new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function PropostasNativasLead({ leadId, lead, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(null);
  const [versaoDe, setVersaoDe] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/proposta-nativa", { params: { lead_id: leadId } });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Falha ao carregar propostas");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const baixar = async (p) => {
    try {
      const { data } = await api.get(`/proposta-nativa/${p.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proposta_${p.numero || p.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao baixar PDF");
    }
  };

  const enviarEmail = async (p) => {
    if (!p.cliente_email) {
      toast.error("Cliente sem e-mail cadastrado");
      return;
    }
    const msg = p.status === "enviada"
      ? `A proposta ${p.numero} já foi enviada. Reenviar para ${p.cliente_email}?`
      : `Enviar a proposta ${p.numero} para ${p.cliente_email}?`;
    if (!window.confirm(msg)) return;
    setEnviando(p.id);
    try {
      const { data } = await api.post(`/proposta-nativa/${p.id}/enviar`);
      toast.success(`Proposta enviada (${data.email_mode})`);
      if (data.link_assinatura) {
        // copia link p/ clipboard
        try { navigator.clipboard.writeText(data.link_assinatura); } catch {}
      }
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setEnviando(null);
    }
  };

  const copiarLink = async (p) => {
    if (!p.token_assinatura) {
      toast.error("Proposta ainda não enviada");
      return;
    }
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const link = `${baseUrl}/assinar-proposta/${p.token_assinatura}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link de assinatura copiado");
    } catch {
      toast.info(link);
    }
  };

  const remover = async (p) => {
    if (!window.confirm(`Remover a proposta ${p.numero}?`)) return;
    try {
      await api.delete(`/proposta-nativa/${p.id}`);
      toast.success("Proposta removida");
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    }
  };

  const novaVersao = (p) => setVersaoDe(p);

  if (loading) {
    return <Card className="p-6 text-center text-muted-foreground">Carregando propostas...</Card>;
  }

  if (items.length === 0) {
    return (
      <div className="space-y-3" data-testid="propostas-aba-vazia">
        {lead && (
          <Card className="p-4 bg-blue-50 border-blue-200 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-blue-900">Pronto para criar a proposta?</div>
              <div className="text-xs text-blue-700">Use o wizard de 4 etapas com IA + inventário + condições + modelo de contrato unificados.</div>
            </div>
            <PropostaNativaForm
              lead={lead}
              forceEnable
              triggerLabel="Montar proposta"
              onCreated={() => { load(); onChange?.(); }}
            />
          </Card>
        )}
        <Card className="p-8 text-center" data-testid="propostas-nativas-vazio">
          <FileText size={32} className="mx-auto text-muted-foreground mb-2" />
          <div className="text-muted-foreground text-sm">
            Nenhuma proposta criada ainda para este lead.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="propostas-nativas-list">
      {lead && (
        <div className="flex items-center justify-between gap-3 p-2 border-b">
          <div className="text-xs text-muted-foreground">
            {items.length} proposta{items.length === 1 ? "" : "s"} para este lead
          </div>
          <PropostaNativaForm
            lead={lead}
            forceEnable
            triggerLabel="Montar nova proposta"
            onCreated={() => { load(); onChange?.(); }}
          />
        </div>
      )}
      {items.map((p) => {
        const opens = p.opens_count || 0;
        const clicks = p.clicks_count || 0;
        const alta = opens >= 2 && p.status === "enviada";  // 🔥 alta temperatura

        return (
        <Card key={p.id} className="p-4" data-testid={`proposta-${p.id}`}>
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <FileText size={18} style={{ color: "#1B3A6B" }} />
                <div className="font-display font-bold" style={{ color: "#1B3A6B" }}>
                  Proposta nº {p.numero}{(p.versao || 1) > 1 ? <span className="text-amber-700 ml-1">v{p.versao}</span> : null}
                </div>
                <Badge className={STATUS_COLOR[p.status] || "bg-slate-100"} variant="secondary" data-testid={`status-${p.id}`}>
                  {p.status}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {TIPO_LABEL[p.tipo_contrato] || p.tipo_contrato}
                </Badge>
                {alta && (
                  <Badge
                    className="text-white border-0"
                    style={{ background: "linear-gradient(90deg, #F97316, #DC2626)" }}
                    data-testid={`alta-temp-${p.id}`}
                    title={`Cliente abriu o e-mail ${opens}x sem assinar — momento ideal para follow-up`}
                  >
                    <Flame size={12} className="mr-1" /> Alta temperatura · abriu {opens}x
                  </Badge>
                )}
                {p.status === "assinada" && (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid={`assinada-${p.id}`}>
                    <CheckCircle2 size={11} className="mr-1" /> Assinada por {p.assinante_nome}
                  </Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground mb-1">
                {p.cliente_nome} · {p.cliente_email}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <div>
                  <span className="text-muted-foreground">Itens:</span>{" "}
                  <strong>{p.itens?.length || 0}</strong>
                </div>
                <div>
                  <span className="text-muted-foreground">{p.tipo_contrato === "locacao" ? "Mensal:" : "Total:"}</span>{" "}
                  <strong>{fmtBRL(p.tipo_contrato === "locacao" ? p.valor_mensal_total : p.valor_total_geral)}</strong>
                </div>
                {p.tipo_contrato === "locacao" && (
                  <div>
                    <span className="text-muted-foreground">Total contrato:</span>{" "}
                    <strong style={{ color: "#F97316" }}>{fmtBRL(p.valor_total_geral)}</strong>
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar size={12} /> {fmtData(p.criado_em)}
                </div>
              </div>
              {p.status === "enviada" && (
                <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">📧 Enviada em {fmtData(p.enviada_em)}</span>
                  {opens > 0 && <span className="inline-flex items-center gap-1 text-blue-600"><Eye size={11} /> Aberta {opens}× · último: {fmtData(p.last_opened_at)}</span>}
                  {clicks > 0 && <span className="inline-flex items-center gap-1 text-emerald-700">🔗 Link clicado {clicks}× · último: {fmtData(p.last_clicked_at)}</span>}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1 items-end">
              {(p.status === "gerada" || p.status === "rascunho") && (
                <Button
                  size="sm"
                  onClick={() => enviarEmail(p)}
                  disabled={enviando === p.id}
                  data-testid={`enviar-${p.id}`}
                  className="text-white w-full"
                  style={{ background: "#F97316" }}
                >
                  <Send size={14} className="mr-1" /> {enviando === p.id ? "Enviando..." : "Enviar"}
                </Button>
              )}
              {p.status === "enviada" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => copiarLink(p)} data-testid={`copiar-link-${p.id}`}>
                    <Copy size={14} className="mr-1" /> Copiar link
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => enviarEmail(p)} disabled={enviando === p.id} data-testid={`reenviar-${p.id}`}>
                    <Send size={14} className="mr-1" /> Reenviar
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" onClick={() => baixar(p)} data-testid={`baixar-${p.id}`}>
                <Download size={14} className="mr-1" /> PDF
              </Button>
              {["enviada", "assinada", "recusada", "expirada"].includes(p.status) && (
                <Button size="sm" variant="outline" onClick={() => novaVersao(p)} data-testid={`nova-versao-${p.id}`} title="Criar nova versão desta proposta">
                  <GitBranch size={14} className="mr-1" /> Nova versão
                </Button>
              )}
              {(p.status === "rascunho" || p.status === "gerada") && (
                <Button size="sm" variant="ghost" onClick={() => remover(p)} data-testid={`remover-${p.id}`}>
                  <Trash2 size={14} className="text-red-500" />
                </Button>
              )}
            </div>
          </div>
        </Card>
      );})}
      <NovaVersaoDialog proposta={versaoDe} onClose={() => setVersaoDe(null)} onCreated={() => { load(); onChange?.(); }} />
    </div>
  );
}

